import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormBuilder, NgModel } from '@angular/forms';
import { passwordValidator, PasswordsMatchValidation } from '@app/shared/utils/validators';
import { UserService, UserServiceOptions } from '@app/origami-api/services/user.service';
import { ErrorCode } from '@app/origami-api/constants';
import { NotificationsService, AuthService, PopupWindowService } from '@app/services';
import { LoginPageSharedService } from '@app/pages/auth/services/login-page-shared.service';
import { GoToLoginService } from '@app/pages/auth/services/go-to-login.service';
import { PASSWORD_MIN_LENGTH } from '@app/pages/auth/constants/password-min-length';
import { getServerConfig } from '@app/origami-api/server-config';

@Component({
  standalone: false,
  selector: 'ti-set-new-password',
  templateUrl: './set-new-password.component.html',
  styleUrls: ['./set-new-password.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SetNewPasswordComponent implements OnInit {
  strings = tiStrings.auth.passwordReset;
  passwordStrings = tiStrings.app.password;
  errStrings = tiStrings.app.formControlErrors;

  @Input() needOldPassword;
  @Input() resetPassCode;
  @Input() resetPasswordQuestionsAnswers: UserServiceOptions.ResetQuestion[];
  @Input() modalUseMode = false;
  @Output() passwordUpdated = new EventEmitter();
  oldPassword;
  password;
  confirmPassword;

  firstSubmit = true;

  changePasswordForm: UntypedFormGroup;

  readonly passwordMinLength = PASSWORD_MIN_LENGTH;

  @ViewChild('oldPsw') oldPasswordModel: NgModel;

  useValidationMessageSetting: boolean = getServerConfig().UseValidationMessageSetting;
  passwordValidationMessage: string = getServerConfig().PasswordValidationMessage;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly userService: UserService,
    private readonly authService: AuthService,
    private readonly notificationsService: NotificationsService,
    private readonly loginPageSharedService: LoginPageSharedService,
    public readonly goToLoginService: GoToLoginService,
    private readonly cdr: ChangeDetectorRef,
    private readonly popupWindowService: PopupWindowService,
  ) {}

  ngOnInit() {
    this.buildFormChangePassword(this.needOldPassword);
    this.onFormChangePasswordChanges();
  }

  private buildFormChangePassword(needOldPassword?: boolean): void {
    this.changePasswordForm = this.fb.group(
      {
        password: [this.password, [Validators.required, Validators.minLength(8), passwordValidator]],
        confirmPassword: [this.confirmPassword, [Validators.required]],
      },
      {
        validator: PasswordsMatchValidation.MatchPassword2,
      },
    );

    if (needOldPassword) {
      this.oldPassword = this.loginPageSharedService.loginPassword;
    }
  }

  private onFormChangePasswordChanges(): void {
    this.changePasswordForm.valueChanges.subscribe((data) => {
      this.password = data.password;
      this.confirmPassword = data.confirmPassword;
    });
  }

  setNewPassword(): void {
    if (this.firstSubmit) {
      this.firstSubmit = false;
    }

    if ((this.oldPasswordModel && this.oldPasswordModel.invalid) || this.changePasswordForm.invalid) {
      return;
    }

    if (this.needOldPassword) {
      // TODO: should be written more understandable code, for example changePassByUser
      this.userService
        .changeUserPassword({
          password: this.oldPassword,
          newPassword: this.password,
        })
        .subscribe((res) => {
          if (res.ErrorCode === ErrorCode.SUCCESS) {
            if (!res.Result.Link) {
              // not IAM
              this.passwordUpdated.emit();
              this.notificationsService.showSuccess(
                this.strings.changeUserPassword,
                this.strings.yourPasswordWasUpdated,
              );
            } else {
              this.popupWindowService.openPopup({
                url: res.Result.Link,
              });
            }
          }
        }, this.errorCallback);
    }

    if (this.resetPassCode) {
      this.userService
        .changeUserPasswordByLink({
          resetPassCode: this.resetPassCode,
          password: this.password,
          passwordResetAnswers: this.resetPasswordQuestionsAnswers,
        })
        .subscribe((res) => {
          if (res.ErrorCode === ErrorCode.SUCCESS) {
            this.passwordUpdated.emit();
            if (this.modalUseMode) {
              this.notificationsService.showSuccess(
                this.strings.changeUserPassword,
                this.strings.yourPasswordWasUpdated,
              );
            }
          }
        }, this.errorCallback);
    }
  }

  private errorCallback = (err) => {
    this.notificationsService.showWarning(err.ErrorCode);
    this.firstSubmit = true;
    this.cdr.detectChanges();
  };
}
